/**
 * Build-time configuration.
 *
 * Everything here comes from `VITE_*` variables, which Vite INLINES into the
 * bundle. That makes them public — visible to anyone who opens the network
 * tab — and the prefix is the mechanism that keeps `APP_SECRET_KEY` out of the
 * JavaScript. Nothing secret may be added to this file.
 */

/** Read a variable, refusing to start rather than defaulting silently. */
function required(name: string, value: string | undefined, fallback?: string): string {
  if (value) return value;
  if (fallback !== undefined) return fallback;
  // A missing API URL that defaulted to something plausible would produce a
  // login screen that fails with a network error and no explanation. Failing
  // at startup names the actual problem.
  throw new Error(
    `${name} is not set. Copy .env.example to .env at the repository root, or set it in the build environment.`,
  );
}

export const config = {
  /** Origin of the API, with no trailing slash. */
  apiBaseUrl: required(
    "VITE_API_BASE_URL",
    import.meta.env.VITE_API_BASE_URL,
    "http://localhost:8000",
  ),

  /**
   * Which tenant this deployment serves.
   *
   * A build-time value because the anonymous routes carry the tenant in the
   * path — `/api/v1/iam/{tenantId}/login` — and there is no principal yet to
   * take it from. One deployment per tenant-facing product; multi-tenancy
   * lives inside the API, not in the bundle.
   */
  tenantId: required("VITE_TENANT_ID", import.meta.env.VITE_TENANT_ID, "acme"),

  /**
   * Sent with a sign-in so the session list is legible.
   *
   * The user agent, trimmed — not a fingerprint. It exists so somebody
   * reviewing their sessions can tell "Firefox on Linux" from "the phone I
   * lost", which is the only thing that list is for.
   */
  deviceName: typeof navigator === "undefined" ? "web" : navigator.userAgent.slice(0, 100),

  /**
   * The server's upload limit, mirrored so a too-large file can be refused
   * before it is sent.
   *
   * Mirrored, not authoritative: the server checks the bytes it actually
   * received, which is the check that counts and the one that cannot be
   * bypassed. This one exists so the refusal is instant rather than arriving
   * after a long upload. Keep it in step with `APP_MAX_UPLOAD_BYTES`; being
   * out of step is a worse user experience, never a security hole.
   */
  maxUploadBytes: Number(import.meta.env.VITE_MAX_UPLOAD_BYTES ?? 10 * 1024 * 1024),
} as const;
